<template>
  <section id="certificate" class="py-16 bg-gray-50">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-5xl mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-[clamp(1.5rem,3vw,2.5rem)] font-bold text-dark">证书鉴别</h2>
          <p class="mt-4 text-gray-medium max-w-2xl mx-auto">专业的证书鉴别服务，快速验证证书真伪，为您的职业发展保驾护航</p>
        </div>

        <div class="bg-white rounded-2xl shadow-card overflow-hidden">
          <div class="grid grid-cols-1 lg:grid-cols-2">
            <div class="p-8 lg:p-10">
              <h3 class="text-xl font-bold mb-6">证书信息验证</h3>
              <form @submit.prevent="submitCertificate" class="space-y-5">
                <div>
                  <label for="name" class="block text-sm font-medium text-gray-dark mb-1">姓名</label>
                  <input 
                    type="text" 
                    id="name" 
                    v-model="form.name"
                    class="w-full px-4 py-2 border border-gray-light rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-custom" 
                    placeholder="请输入您的姓名"
                    required
                  >
                </div>
                <div>
                  <label for="phone" class="block text-sm font-medium text-gray-dark mb-1">联系方式</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    v-model="form.phone"
                    class="w-full px-4 py-2 border border-gray-light rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-custom" 
                    placeholder="请输入您的手机号码"
                    required
                  >
                </div>
                <div>
                  <label for="certType" class="block text-sm font-medium text-gray-dark mb-1">证书类型</label>
                  <select 
                    id="certType" 
                    v-model="form.certType"
                    class="w-full px-4 py-2 border border-gray-light rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-custom bg-white" 
                    required
                  >
                    <option value="" disabled>请选择证书类型</option>
                    <option value="职业资格证书">职业资格证书</option>
                    <option value="职业技能等级证书">职业技能等级证书</option>
                    <option value="专项职业能力证书">专项职业能力证书</option>
                    <option value="其他类型证书">其他类型证书</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-dark mb-1">证书照片</label>
                  
                  <!-- 图片预览区域 -->
                  <div v-if="uploadedImages.length > 0" class="mb-3 grid grid-cols-3 gap-3">
                    <div v-for="(image, index) in uploadedImages" :key="index" class="relative group">
                      <img :src="image.fullUrl" :alt="`证书照片${index + 1}`" class="w-full h-24 object-cover rounded-lg border-2 border-gray-light">
                      <button 
                        @click="removeImage(index)" 
                        type="button"
                        class="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <i class="fa-solid fa-times text-xs"></i>
                      </button>
                    </div>
                  </div>
                  
                  <!-- 上传按钮 -->
                  <div class="border-2 border-dashed border-gray-light rounded-lg p-6 text-center hover:border-primary transition-custom cursor-pointer bg-gray-50">
                    <input 
                      type="file" 
                      class="hidden" 
                      id="certImage" 
                      @change="handleFileUpload" 
                      accept="image/jpeg,image/jpg,image/png"
                      multiple
                      ref="fileInput"
                    >
                    <label for="certImage" class="cursor-pointer">
                      <i class="fa-solid fa-cloud-arrow-up text-3xl text-gray-medium mb-2"></i>
                      <p class="text-sm text-gray-medium">点击上传证书照片</p>
                      <p class="text-xs text-gray-medium mt-1">支持JPG、PNG格式</p>
                      <p v-if="uploadedImages.length > 0" class="text-xs text-primary mt-1">已上传 {{ uploadedImages.length }} 张</p>
                    </label>
                  </div>
                  
                  <!-- 上传进度 -->
                  <div v-if="uploading" class="mt-3">
                    <div class="w-full bg-gray-200 rounded-full h-2">
                      <div class="bg-primary h-2 rounded-full transition-all" :style="{ width: uploadProgress + '%' }"></div>
                    </div>
                    <p class="text-xs text-gray-600 mt-1 text-center">上传中... {{ uploadProgress }}%</p>
                  </div>
                </div>
                <button type="submit" class="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-custom shadow-sm">
                  提交鉴别申请
                </button>
              </form>
            </div>
            <div class="hidden lg:block bg-primary/5 p-8 lg:p-10 flex flex-col justify-center">
              <div class="mb-8">
                <div class="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                  <i class="fa-solid fa-shield-halved text-primary text-2xl"></i>
                </div>
                <h3 class="text-xl font-bold mb-2">专业鉴别</h3>
                <p class="text-gray-medium">由行业专家进行证书真伪鉴别，确保结果准确可靠</p>
              </div>
              <div class="mb-8">
                <div class="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                  <i class="fa-regular fa-clock text-primary text-2xl"></i>
                </div>
                <h3 class="text-xl font-bold mb-2">快速响应</h3>
                <p class="text-gray-medium">提交申请后24小时内完成鉴别，及时反馈结果</p>
              </div>
              <div>
                <div class="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                  <i class="fa-solid fa-lock text-primary text-2xl"></i>
                </div>
                <h3 class="text-xl font-bold mb-2">信息保密</h3>
                <p class="text-gray-medium">严格保密您的个人信息和证书信息，保障信息安全</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { uploadFile, verifyCertificate } from '@/api'

export default {
  name: 'CertificateSection',
  data() {
    return {
      form: {
        name: '',
        phone: '',
        certType: ''
      },
      uploadedImages: [], // 存储已上传的图片信息 [{ path: '/uploads/xxx.jpg', fullUrl: 'http://....' }]
      uploading: false,
      uploadProgress: 0
    }
  },
  methods: {
    async handleFileUpload(event) {
      const files = Array.from(event.target.files)
      
      if (files.length === 0) return
      
      // 验证文件格式
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      
      for (const file of files) {
        // 验证文件类型
        if (!allowedTypes.includes(file.type)) {
          this.$message?.error(`文件 ${file.name} 格式不支持，仅支持JPG、PNG格式`)
          event.target.value = ''
          return
        }
      }
      
      // 上传文件
      this.uploading = true
      this.uploadProgress = 0
      
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          const formData = new FormData()
          formData.append('file', file)
          
          // 模拟进度
          const progressInterval = setInterval(() => {
            if (this.uploadProgress < 90) {
              this.uploadProgress = Math.min(90, this.uploadProgress + 10)
            }
          }, 100)
          
          const response = await uploadFile(formData)
          
          clearInterval(progressInterval)
          this.uploadProgress = ((i + 1) / files.length) * 100
          
          // 后端返回格式：{ data: { url: '/uploads/xxx.jpg', fullurl: 'http://domain.com/uploads/xxx.jpg' } }
          // url: 相对路径，用于提交给后端
          // fullurl: 完整URL，用于图片回显预览
          this.uploadedImages.push({
            path: response.data.url,       // 相对路径，用于提交给后端
            fullUrl: response.data.fullurl // 完整URL，用于预览显示
          })
        }
        
        this.$message?.success('图片上传成功')
        
      } catch (error) {
        console.error('上传失败:', error)
        this.$message?.error(error.msg || error.message || '图片上传失败，请稍后重试')
      } finally {
        this.uploading = false
        this.uploadProgress = 0
        // 清空input，允许重复上传相同文件
        event.target.value = ''
      }
    },
    
    removeImage(index) {
      this.uploadedImages.splice(index, 1)
      this.$message?.success('已删除图片')
    },
    
    async submitCertificate() {
      // 验证表单
      if (!this.form.name || !this.form.phone || !this.form.certType) {
        this.$message?.warning('请填写所有必填项')
        return
      }

      // 验证手机号格式
      const phoneRegex = /^1[3-9]\d{9}$/
      if (!phoneRegex.test(this.form.phone)) {
        this.$message?.warning('请输入正确的手机号码')
        return
      }
      
      // 验证是否上传了照片
      if (this.uploadedImages.length === 0) {
        this.$message?.warning('请至少上传一张证书照片')
        return
      }

      try {
        // 将图片路径转换为逗号分隔的字符串
        const imageUrl = this.uploadedImages.map(img => img.path).join(',')
        
        const submitData = {
          name: this.form.name,
          phone: this.form.phone,
          cerType: this.form.certType,
          image_url: imageUrl // 逗号分隔的路径字符串，不带完整域名
        }
        
        console.log('提交证书验证数据:', submitData)
        
        // 调用证书验证接口
        await verifyCertificate(submitData)

        // 提交成功提示
        this.$message?.success('证书鉴别申请已成功提交，请等待后台鉴定审核~')
        
        // 重置表单
        this.form = {
          name: '',
          phone: '',
          certType: ''
        }
        this.uploadedImages = []
        
      } catch (error) {
        console.error('提交失败:', error)
        this.$message?.error(error.msg || error.message || '提交失败，请稍后重试')
      }
    }
  }
}
</script>

<style lang="less" scoped>
.transition-custom {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
</style> 